import React from "react";
import { MessageCircle, Phone, Mail, MapPin, Clock, ExternalLink } from "lucide-react";
import { useLang } from "../LanguageContext";
import { RESTAURANT, telLink, waLink } from "../restaurant.config";

export default function Contatti() {
  const { t } = useLang();

  return (
    <section
      id="contatti"
      data-testid="contatti-section"
      className="bg-[#F5F2EC] text-[#23211E] py-24 md:py-40 px-6 md:px-10"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-12 gap-8 md:gap-12 mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-6 reveal-on-scroll">
            <p className="text-[11px] uppercase tracking-[0.35em] text-[#565F41] mb-6">
              {t.contatti.eyebrow}
            </p>
            <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] tracking-tight whitespace-pre-line">
              {t.contatti.title}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 flex items-end reveal-on-scroll">
            <p className="text-base md:text-lg font-light leading-relaxed text-[#3D3A35]">
              {t.contatti.intro}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-8 md:gap-12">
          {/* Left: info */}
          <div className="col-span-12 lg:col-span-5 space-y-10">
            {/* Reservations block */}
            <div
              data-testid="reservations-block"
              className="border border-[#23211E]/15 p-8"
            >
              <p className="text-[10px] uppercase tracking-[0.35em] text-[#B85532] mb-6">
                {t.contatti.reservationsLabel}
              </p>
              <div className="space-y-3">
                <a
                  data-testid="reservation-whatsapp-link"
                  href={waLink(t.contatti.prefill)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-between gap-4 py-3 border-b border-[#23211E]/15"
                >
                  <span className="flex items-center gap-3">
                    <MessageCircle className="w-5 h-5 text-[#B85532]" strokeWidth={1.4} />
                    <span className="font-serif text-lg md:text-xl">
                      {t.contatti.whatsappCta}
                    </span>
                  </span>
                  <span className="text-sm text-[#3D3A35] group-hover:text-[#B85532] transition-colors">
                    {RESTAURANT.mobileDisplay}
                  </span>
                </a>
                <a
                  data-testid="reservation-phone-link"
                  href={telLink("mobile")}
                  className="group flex items-center justify-between gap-4 py-3"
                >
                  <span className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-[#B85532]" strokeWidth={1.4} />
                    <span className="font-serif text-lg md:text-xl">
                      {t.contatti.callCta}
                    </span>
                  </span>
                  <span className="text-sm text-[#3D3A35] group-hover:text-[#B85532] transition-colors">
                    {RESTAURANT.mobileDisplay}
                  </span>
                </a>
              </div>
            </div>

            {/* Meta info */}
            <ul className="space-y-6">
              <li className="flex gap-4" data-testid="contact-address">
                <MapPin className="w-5 h-5 mt-1 text-[#565F41]" strokeWidth={1.4} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#565F41] mb-1">
                    {t.contatti.addressLabel}
                  </p>
                  <p className="font-serif text-lg leading-snug">
                    {RESTAURANT.address.street}
                    <br />
                    {RESTAURANT.address.cap} {RESTAURANT.address.city} ({RESTAURANT.address.province})
                  </p>
                  <a
                    data-testid="contact-maps-link"
                    href={RESTAURANT.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 text-xs uppercase tracking-[0.22em] text-[#B85532] hover:underline"
                  >
                    {t.contatti.mapsCta}
                    <ExternalLink className="w-3 h-3" strokeWidth={1.6} />
                  </a>
                </div>
              </li>
              <li className="flex gap-4" data-testid="contact-hours">
                <Clock className="w-5 h-5 mt-1 text-[#565F41]" strokeWidth={1.4} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#565F41] mb-1">
                    {t.contatti.hoursLabel}
                  </p>
                  <ul className="font-serif text-lg leading-snug space-y-0.5">
                    {t.contatti.hoursValue.map((line, i) => (
                      <li key={i}>{line}</li>
                    ))}
                  </ul>
                  {t.contatti.hoursNote && (
                    <p
                      data-testid="contact-hours-note"
                      className="mt-4 text-sm leading-relaxed text-[#3D3A35] font-light max-w-prose"
                    >
                      {t.contatti.hoursNote}
                    </p>
                  )}
                  <div
                    data-testid="contact-bar"
                    className="mt-4 pt-4 border-t border-[#23211E]/10"
                  >
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#B85532] mb-1">
                      {t.contatti.barLabel}
                    </p>
                    <p className="font-serif text-base leading-snug text-[#3D3A35]">
                      {t.contatti.barValue}
                    </p>
                  </div>
                </div>
              </li>
              <li className="flex gap-4" data-testid="contact-phones">
                <Phone className="w-5 h-5 mt-1 text-[#565F41]" strokeWidth={1.4} />
                <div className="space-y-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#565F41] mb-1">
                      {t.contatti.phoneLabel}
                    </p>
                    <a
                      href={telLink("mobile")}
                      className="font-serif text-lg hover:text-[#B85532] transition-colors"
                    >
                      {RESTAURANT.mobileDisplay}
                    </a>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-[#565F41] mb-1">
                      {t.contatti.landlineLabel}
                    </p>
                    <a
                      href={telLink("landline")}
                      className="font-serif text-lg hover:text-[#B85532] transition-colors"
                    >
                      {RESTAURANT.landlineDisplay}
                    </a>
                  </div>
                </div>
              </li>
              <li className="flex gap-4" data-testid="contact-email">
                <Mail className="w-5 h-5 mt-1 text-[#565F41]" strokeWidth={1.4} />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#565F41] mb-1">
                    {t.contatti.emailLabel}
                  </p>
                  <a
                    href={`mailto:${RESTAURANT.email}`}
                    className="font-serif text-lg hover:text-[#B85532] transition-colors break-all"
                  >
                    {RESTAURANT.email}
                  </a>
                </div>
              </li>
            </ul>
          </div>

          {/* Right: map */}
          <div className="col-span-12 lg:col-span-7">
            <div
              data-testid="google-map"
              className="w-full h-[500px] lg:h-full min-h-[500px] border border-[#23211E]/15 overflow-hidden bg-[#EBE7DD]"
            >
              <iframe
                title="Grappolo d'Oro — Google Maps"
                src={RESTAURANT.mapsEmbed}
                className="w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
